// hooks/features/sms/useSms.ts

import { useState, useCallback, useEffect } from "react";
import { smsService } from "../../../services/smsService";
import { Sms } from "../../../types/features/Sms";

export default function useSms() {
  const [smsList, setSmsList] = useState<Sms[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const getSmsList = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response = await smsService.getSmsList(page, perPage);
      setSmsList(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors du chargement des SMS");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const getSmsDetails = useCallback(async (id: string): Promise<Sms | null> => {
    setLoading(true);
    setError(null);
    try {
      const sms = await smsService.getSmsById(id);
      return sms;
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors du chargement du SMS");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getSmsStatus = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const status = await smsService.getSmsStatus(id);
      return status;
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors du chargement du statut");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const sendSms = useCallback(async (data: any): Promise<Sms | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await smsService.sendSms(data);
      setSmsList((prev) => [response.data, ...prev]);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi du SMS");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const resendSms = useCallback(async (smsId: string, recipientId: string): Promise<any> => {
    setLoading(true);
    setError(null);
    try {
      const response = await smsService.resendSms(smsId, recipientId);
      // Mettre à jour le statut du destinataire dans la liste
      setSmsList((prev) =>
        prev.map((sms) => {
          if (sms.id === smsId) {
            const updatedRecipients = sms.recipients?.map((r) => {
              if (r.id === recipientId) {
                return { ...r, status: "pending" as const };
              }
              return r;
            });
            return { ...sms, recipients: updatedRecipients };
          }
          return sms;
        })
      );
      return response;
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors du renvoi du SMS");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteSms = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      // await smsService.deleteSms(id);
      setSmsList((prev) => prev.filter((sms) => sms.id !== id));
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de la suppression du SMS");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteMultipleSms = useCallback(async (ids: string[]): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      for (const id of ids) {
        // await smsService.deleteSms(id);
      }
      setSmsList((prev) => prev.filter((sms) => !ids.includes(sms.id)));
      return true;
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de la suppression multiple");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshStatus = useCallback(
    async (id: string) => {
      setLoading(true);
      setError(null);
      try {
        // Rafraîchir le statut d'un SMS (appel Twilio)
        await smsService.getSmsStatus(id);
        await getSmsList(pagination.currentPage, pagination.perPage);
      } catch (err: any) {
        setError(err.response?.data?.message || "Erreur lors du rafraîchissement");
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [pagination.currentPage, pagination.perPage, getSmsList]
  );

  useEffect(() => {
    getSmsList();
  }, []);

  return {
    smsList,
    loading,
    error,
    pagination,
    getSmsList,
    getSmsDetails,
    getSmsStatus,
    sendSms,
    resendSms,
    deleteSms,
    deleteMultipleSms,
    refreshStatus,
  };
}
